import {
  BooleanColumn,
  DateColumn,
  BreakWordColumn,
  MultiselectColumn,
  EnhancedDetailsList,
  ProcessesColumn,
  SelectColumn,
  StringColumn,
  UserDtoColumn
} from '../../common/EnhancedDetailsList';
import {ColumnKey, IEnhancedColumn} from '../../common/EnhancedDetailsList/IEnhancedDetailsList';
import {Unit} from '../../../models';
import {Icon, Spinner, TooltipHost} from '@fluentui/react';
import {useContext, useEffect, useMemo, useState} from 'react';
import * as useUnit from '../../../hooks/services/useUnit';
import {DictionariesContext} from '../../../providers';
import {OpenPDCColumn} from '../../common/EnhancedDetailsList/ColumnTypes/OpenPDCColumn';
import {Helmet} from 'react-helmet';
import {StringArrayColumn} from '../../common/EnhancedDetailsList/ColumnTypes/StringArrayColumn';

import {IDocumentsPerProcessProps} from './IDocumentsPerProcessProps';
import {useAdmin} from '../../../hooks/services/useAdmin';
import {DocumentPerProcessAdminRequestDto} from '../../../services/generated/models/DocumentPerProcessAdminRequestDto';
import {DocumentPerProcess} from '../../../models/DocumentPerProcess';

export const DocumentsPerProcess: React.FC<IDocumentsPerProcessProps> = ({
  unitId,
  unitIds,
  processIds,
  startSearch,
  setStartSearch
}) => {
  const {getUnit} = useUnit.useUnit();

  const {getUnitDocumentsPerProcess} = useUnit.useUnit();
  const {getAdminDocumentPerProcess} = useAdmin();
  const [unitTitle, setUnitTitle] = useState<string>();
  const {globalFiltersFunction} = useContext(DictionariesContext);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [documents, setDocuments] = useState<DocumentPerProcess[] | null>(null);

  const prepareUnitTitle = (unit: Unit): string => {
    if (unit.title) {
      return unit.parentTitle && unit.unitTypeId === 2
        ? `${unit.parentTitle} ${unit.title}`
        : unit.title;
    }
    return '';
  };
  useEffect(() => {
    (async () => {
      if (!startSearch) {
        return;
      }
      setIsLoading(true);
      var data: DocumentPerProcessAdminRequestDto = {
        unitIds: unitIds ?? [],
        processIds: processIds ?? []
      };
      const documentsResponse = await getAdminDocumentPerProcess(data);
      setDocuments(documentsResponse.result);

      setIsLoading(false);
      if (setStartSearch) {
        setStartSearch(false);
      }
    })();
  }, [getAdminDocumentPerProcess, setStartSearch, startSearch, processIds, unitIds]);

  useEffect(() => {
    (async () => {
      if (!unitId) {
        return;
      }
      setIsLoading(true);
      const simpleUnit = await getUnit(unitId);
      const documentsResponse = await getUnitDocumentsPerProcess(unitId);
      setDocuments(documentsResponse.result);
      if (simpleUnit && simpleUnit.result) {
        setUnitTitle(prepareUnitTitle(simpleUnit.result));
      }
      setIsLoading(false);
    })();
  }, [getUnitDocumentsPerProcess, unitId, getUnit]);

  const [itemsFiltered, setItemsFiltered] = useState<boolean>(false);

  const filteredDocuments = useMemo((): DocumentPerProcess[] => {
    var result = documents ? documents.filter(globalFiltersFunction) : [];
    if (documents && documents.length > 0 && result.length !== documents.length) {
      setItemsFiltered(true);
    } else {
      setItemsFiltered(false);
    }

    return result;
  }, [documents, globalFiltersFunction]);

  const isSmart = useMemo(
    () => typeof new URLSearchParams(document.location.search).get('smart') === 'string',
    []
  );
  const columns = useMemo(() => {
    const cols: IEnhancedColumn<DocumentPerProcess>[] = [
      {
        key: ColumnKey.Url,
        name: '',
        data: OpenPDCColumn
      },
      ...(isSmart
        ? ([
            {
              key: 'id',
              name: 'ID',
              minWidth: 130
            }
          ] as IEnhancedColumn<DocumentPerProcess>[])
        : []),
      {
        key: ColumnKey.Title,
        name: 'Title',
        minWidth: 380,
        flexGrow: 1,
        data: BreakWordColumn,
        exportToExcel: true,
        onExportToExcel: (item, column) => {
          return {
            text: item.title,
            link: item.url
          };
        }
      },
      {
        key: ColumnKey.DocumentId,
        name: 'Document ID',
        minWidth: 135,
        exportToExcel: true,
        onRender(item) {
          return (
            <>
              {item.documentId}
              {item.inReview && (
                <TooltipHost content="New revision is pending">
                  <Icon styles={{root: {paddingLeft: '5px', color: 'red'}}} iconName="Sync" />
                </TooltipHost>
              )}
              {item.isOutdated && !item.inReview && (
                <TooltipHost content="This document has not been validated, and may contain outdated or inaccurate information. Use only after consulting the owner of this document">
                  <Icon styles={{root: {paddingLeft: '5px', color: 'red'}}} iconName="Warning" />
                </TooltipHost>
              )}
            </>
          );
        }
      },
      {
        key: ColumnKey.ContentTypeString,
        name: 'Content Type',
        data: SelectColumn,
        minWidth: 135,
        exportToExcel: true
      },
      {
        key: 'isInherited',
        name: 'Is Inherited',
        data: BooleanColumn,
        minWidth: 150,
        exportToExcel: true
      },
      {
        key: ColumnKey.Process,
        name: 'Process(es)',
        data: ProcessesColumn,
        minWidth: 135,
        exportToExcel: true
      },
      {
        key: ColumnKey.ProcessOwner,
        name: 'Process Owner',
        minWidth: 180,
        data: UserDtoColumn,
        exportToExcel: true
      },
      {
        key: ColumnKey.SecondaryProcessOwners,
        name: 'Secondary Owners',
        minWidth: 180,
        data: UserDtoColumn,
        exportToExcel: true
      },
      {
        key: ColumnKey.DocumentKind,
        name: 'Document Kind',
        data: SelectColumn,
        minWidth: 150,
        exportToExcel: true
      },
      {
        key: ColumnKey.Revision,
        name: 'Revision',
        data: SelectColumn,
        minWidth: 110,
        exportToExcel: true
      },
      {
        key: ColumnKey.OperatingUnit,
        name: 'Operating Unit',
        data: SelectColumn,
        minWidth: 150,
        exportToExcel: true
      },
      {
        key: ColumnKey.FunctionString,
        name: 'Function',
        data: SelectColumn,
        minWidth: 105,
        exportToExcel: true
      },
      {
        key: 'businessLines',
        name: 'Business Line',
        data: MultiselectColumn,
        minWidth: 140,
        exportToExcel: true
      },

      {
        key: 'industries',
        name: 'Industry',
        data: MultiselectColumn,
        minWidth: 130,
        exportToExcel: true
      },
      {
        key: 'specialities',
        name: 'Specialty',
        data: MultiselectColumn,
        minWidth: 110,
        exportToExcel: true
      },
      {
        key: 'projectSizeCategories',
        name: 'Project Size Category',
        data: MultiselectColumn,
        minWidth: 190,
        exportToExcel: true
      },
      {
        key: ColumnKey.DocumentOwner,
        name: 'Document Owner',
        minWidth: 160,
        exportToExcel: true
      },
      {
        key: ColumnKey.ApprovedDate,
        name: 'Approved Date',
        minWidth: 145,
        data: DateColumn,
        exportToExcel: true
      },
      {
        key: ColumnKey.NextConfirmationDate,
        exportToExcel: true,
        name: 'Next Confirmation Date',
        minWidth: 145,
        data: DateColumn
      },
      {
        key: ColumnKey.ApprovedBy,
        name: 'Approver',
        minWidth: 145,
        data: StringColumn,
        exportToExcel: true
      },
      {
        key: ColumnKey.Auditable,
        name: 'Auditable',
        data: BooleanColumn,
        minWidth: 110,
        exportToExcel: true
      },
      {
        key: ColumnKey.RelevantTo,
        name: 'Related To',
        minWidth: 135,
        exportToExcel: true
      },
      {
        key: ColumnKey.Language,
        name: 'Language',
        data: SelectColumn,
        minWidth: 110,
        exportToExcel: true
      },
      {
        key: ColumnKey.SecurityLevel,
        name: 'Security Level',
        data: SelectColumn,
        minWidth: 140,
        exportToExcel: true
      },
      {
        key: ColumnKey.KeyWords,
        exportToExcel: true,
        name: 'Keywords',
        minWidth: 190,
        data: StringArrayColumn,
        onExportToExcel: (item, column) => {
          return item.keyWords?.join(', ') || '';
        }
      }
    ];
    return cols;
  }, [isSmart]);

  return (
    <>
      {unitTitle && (
        <Helmet>
          <title>IMS {unitTitle} Documents per Process List</title>
        </Helmet>
      )}
      {!unitTitle && (
        <Helmet>
          <title>IMS Documents per Process List</title>
        </Helmet>
      )}
      {isLoading ? <Spinner /> : null}
      {isLoading !== undefined ? (
        <>
          <EnhancedDetailsList<DocumentPerProcess, DocumentPerProcess, DocumentPerProcess>
            columns={columns}
            items={filteredDocuments}
            itemsFiltered={itemsFiltered}
            pageSize={30}
            excelExport
            frozen
            excelFileName={'Exported Documents per Process List'}
          />
        </>
      ) : null}
    </>
  );
};
